import React from "react";
import { Text, HStack, VStack, StackItem, Popover, IconButton, Icon, Badge } from "@astryxdesign/core";
import { Info } from "lucide-react";
import ScoreBadge from "./ScoreBadge";
import type { ScoreBadgeTier } from "./ScoreBadge";
import styles from "./ScoredQuestionRow.module.css";

export interface ScoredQuestionRowProps {
  index: number;
  body: string;
  score: number | null;
  max: number;
  idealResponse: React.ReactNode;
  /**
   * When provided, an info affordance next to the ScoreBadge opens an Astryx
   * Popover with this content (typically the per-question metric breakdown:
   * evaluability, differentiation, follow-up rate, effectiveness,
   * worth-reviewing). Omit when the question has no scored responses yet.
   */
  metrics?: React.ReactNode;
  /** Passed through to the internal ScoreBadge. @default "fraction" */
  scoreFormat?: "fraction" | "percent";
  /** Passed through to the internal ScoreBadge as a server-authoritative override. */
  scoreTier?: ScoreBadgeTier;
  /** Label above the ideal-response box. @default "Ideal response" */
  responseLabel?: string;
  /**
   * Dims the row and replaces the ScoreBadge with a neutral "Excluded" badge.
   * @default false
   */
  isExcluded?: boolean;
  /**
   * Trailing content rendered after the badge cluster (e.g. an admin
   * exclude/include toggle button).
   */
  endAction?: React.ReactNode;
}

/**
 * Read-only row for a single scored question: number + question body with a
 * ScoreBadge at the trailing edge, and an "Ideal response" box below.
 *
 * Renders via Astryx's Text/Popover/IconButton, so this MUST be rendered
 * inside an Astryx <Theme> scope — and, because the Popover renders into a
 * layer, inside an Astryx <LayerProvider>.
 */
const ScoredQuestionRow = ({
  index,
  body,
  score,
  max,
  idealResponse,
  metrics,
  scoreFormat,
  scoreTier,
  responseLabel = "Ideal response",
  isExcluded = false,
  endAction,
}: ScoredQuestionRowProps): React.ReactElement => {
  return (
    <div className={isExcluded ? `${styles.row} ${styles.excluded}` : styles.row}>
      <HStack gap={3} align="start">
        <StackItem size="fill">
          <Text as="p">
            <span className={styles.index}>{index}.</span>{" "}
            {body || "Untitled question"}
          </Text>
        </StackItem>
        <HStack gap={1} align="center" className={styles.score}>
          {isExcluded ? (
            <Badge variant="neutral" label="Excluded" />
          ) : (
            <ScoreBadge score={score} max={max} format={scoreFormat} tier={scoreTier} />
          )}
          {metrics && (
            <Popover
              label="Question metrics"
              content={metrics}
              placement="below"
              alignment="end"
              width={300}
            >
              <IconButton
                icon={<Icon icon={Info} color="secondary" />}
                label="Show question metrics"
                variant="ghost"
                size="sm"
              />
            </Popover>
          )}
          {endAction}
        </HStack>
      </HStack>

      <VStack gap={1} className={styles.ideal}>
        <Text type="supporting" color="secondary" className={styles.idealLabel}>
          {responseLabel}
        </Text>
        {typeof idealResponse === "string" ? (
          <Text color="secondary">{idealResponse || "—"}</Text>
        ) : (
          idealResponse
        )}
      </VStack>
    </div>
  );
};

export default ScoredQuestionRow;
